import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { lazy, Suspense, useEffect } from "react";

// Critical routes — loaded immediately
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
const TavliGame = lazy(() => import("./pages/TavliGame.tsx"));



// Lazy-loaded routes — downloaded only when needed
const Auth = lazy(() => import("./pages/Auth.tsx"));
const Chat = lazy(() => import("./pages/Chat.tsx"));
const ChatLanding = lazy(() => import("./pages/ChatLanding.tsx"));

const ForgotPassword = lazy(() => import("./pages/ForgotPassword.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const Preferences = lazy(() => import("./pages/Preferences.tsx"));
const StrategoPreferences = lazy(() => import("./pages/StrategoPreferences.tsx"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile.tsx"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage.tsx"));
const TavliLanding = lazy(() => import("./pages/TavliLanding.tsx"));
const TavliGuides = lazy(() => import("./pages/TavliGuides.tsx"));
const TavliPortesRules = lazy(() => import("./pages/TavliPortesRules.tsx"));
const TavliPlakotoRules = lazy(() => import("./pages/TavliPlakotoRules.tsx"));
const TavliFevgaRules = lazy(() => import("./pages/TavliFevgaRules.tsx"));
const TavliLobby = lazy(() => import("./pages/TavliLobby.tsx"));
const StrategoLanding = lazy(() => import("./pages/StrategoLanding.tsx"));
const StrategoLobby = lazy(() => import("./pages/StrategoLobby.tsx"));

const StrategoPreview = lazy(() => import("./pages/StrategoPreview.tsx"));
const StrategoGame = lazy(() => import("./pages/StrategoGame.tsx"));
const StrategoShop = lazy(() => import("./pages/StrategoShop.tsx"));
const StrategoStratigeio = lazy(() => import("./pages/StrategoStratigeio.tsx"));
const StrategoFormationStudio = lazy(() => import("./pages/StrategoFormationStudio.tsx"));
const StrategoHowToPlay = lazy(() => import("./pages/StrategoHowToPlay.tsx"));
const ChatShop = lazy(() => import("./pages/ChatShop.tsx"));
const TavliEmbed = lazy(() => import("./pages/TavliEmbed.tsx"));
const LudoGame = lazy(() => import("./pages/LudoGame.tsx"));
const LudoLobby = lazy(() => import("./pages/LudoLobby.tsx"));
const HangmanLobby = lazy(() => import("./pages/HangmanLobby.tsx"));
const HangmanGame = lazy(() => import("./pages/HangmanGame.tsx"));
const HangmanDuelGame = lazy(() => import("./pages/HangmanDuelGame.tsx"));
const HangmanMultiGame = lazy(() => import("./pages/HangmanMultiGame.tsx"));
const HangmanClassicGame = lazy(() => import("./pages/HangmanClassicGame.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const AboutPlatform = lazy(() => import("./pages/AboutPlatform.tsx"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const AdminBots = lazy(() => import("./pages/AdminBots.tsx"));
const GamesShowcase = lazy(() => import("./pages/GamesShowcase.tsx"));
const SlotsGame = lazy(() => import("./pages/SlotsGame.tsx"));
const StrategoSlotsGame = lazy(() => import("./pages/StrategoSlotsGame.tsx"));
const JukeboxSlotsGame = lazy(() => import("./pages/JukeboxSlotsGame.tsx"));
const WheelOfFate = lazy(() => import("./pages/WheelOfFate.tsx"));
const ArkanoidGamePage = lazy(() => import("./pages/ArkanoidGame.tsx"));
const AgoniaGame = lazy(() => import("./pages/AgoniaGame.tsx"));
const AgoniaLanding = lazy(() => import("./pages/AgoniaLanding.tsx"));
const CardGames = lazy(() => import("./pages/CardGames.tsx"));
const KseriLanding = lazy(() => import("./pages/KseriLanding.tsx"));
const RankingInfo = lazy(() => import("./pages/RankingInfo.tsx"));
const EmbedGamePage = lazy(() => import("./embed/EmbedGamePage.tsx"));
const XeriGame = lazy(() => import("./pages/XeriGame.tsx"));
const GiftCardPreviewPage = lazy(() => import("./pages/GiftCardPreviewPage.tsx"));
const MahjongRedirect = lazy(() => import("./pages/MahjongRedirect.tsx"));
// Προσωρινό εργαστήριο ζαριών — χωρίς κανένα link στην εφαρμογή (μόνο χειροκίνητο URL)
const DiceLabPage = lazy(() => import("./pages/DiceLabPage.tsx"));



const GameInviteNotification = lazy(() => import("./components/game/GameInviteNotification.tsx"));
const TavliPromotionListener = lazy(() => import("./components/game/TavliPromotionListener.tsx"));

const CookieConsent = lazy(() => import("./components/CookieConsent.tsx"));
const AnnouncementBanner = lazy(() => import("./components/AnnouncementBanner.tsx"));
const WelcomeBanner = lazy(() => import("./components/WelcomeBanner.tsx"));
const SitePresenceTracker = lazy(() => import("./components/SitePresenceTracker.tsx"));
const DeviceBanGuard = lazy(() => import("./components/DeviceBanGuard.tsx"));
const NotificationDialog = lazy(() => import("./components/NotificationDialog.tsx"));
const DjGiftReceivedPopup = lazy(() => import("./components/chat/DjGiftReceivedPopup.tsx"));
const PendingGiftsClaimer = lazy(() => import("./components/PendingGiftsClaimer.tsx"));


// Optimized QueryClient with smart defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data fresh for 2 min, refetch in background after that
      staleTime: 2 * 60 * 1000,
      // Garbage-collect unused queries after 5 min
      gcTime: 5 * 60 * 1000,
      // Don't refetch on window focus for most queries (reduces API calls)
      refetchOnWindowFocus: false,
      // Keep retries conservative to avoid backend overload cascades
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(800 * 2 ** attemptIndex, 3000),
    },
    mutations: {
      retry: 1,
    },
  },
});

// Minimal loading spinner for lazy routes
function RouteLoader() {
  return (
    <div className="h-[100dvh] flex items-center justify-center bg-background">
      <div className="h-8 w-8 border-2 border-t-primary border-muted rounded-full animate-spin" />
    </div>
  );
}

function StaticHtmlDocument({ htmlPath }: { htmlPath: string }) {
  useEffect(() => {
    let cancelled = false;

    fetch(htmlPath, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load ${htmlPath}`);
        return response.text();
      })
      .then((html) => {
        if (cancelled) return;
        document.open();
        document.write(html);
        document.close();
      })
      .catch(() => {
        if (!cancelled) window.location.replace(htmlPath);
      });

    return () => {
      cancelled = true;
    };
  }, [htmlPath]);

  return <div className="min-h-screen bg-background" />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation();
  const { user } = useAuth();
  const path = location.pathname;
  const isAdminRoute = path === "/admin" || path === "/admin-config";
  const isAuthRoute = ["/auth", "/forgot-password", "/reset-password"].includes(path);
  const isRegisteredUser = Boolean(user && !user.is_anonymous && (user.email || user.user_metadata?.email));

  // Cost optimization: lazy realtime channels — only mount listeners on routes
  // where their notifications can actually be triggered/received.
  // This dramatically reduces global "Live Updates" usage.
  const isChatRoute = path.startsWith("/chat");
  const isGameRoute =
    path.startsWith("/tavli") ||
    path.startsWith("/ludo") ||
    path.startsWith("/imperium1800") ||
    path.startsWith("/agonia") ||
    path.startsWith("/xeri");
  const isHomeRoute = path === "/";

  // DJ gifts are only sent from chat → only listen there
  const shouldListenDjGifts = isChatRoute;
  // Game invites can only be acted on from home, chat, or game routes
  const shouldListenGameInvites = isHomeRoute || isChatRoute || isGameRoute;
  // Tavli promotion banner is only relevant on home, chat, and tavli pages
  const shouldListenTavliPromo = isHomeRoute || isChatRoute || path.startsWith("/tavli");

  return (
    <>
      <ScrollToTop />
      <DeviceBanGuard />

      {!isAuthRoute && isRegisteredUser && (
        <>
          <SitePresenceTracker />
          <NotificationDialog />
          {shouldListenDjGifts && <DjGiftReceivedPopup />}
          <PendingGiftsClaimer />
        </>
      )}

      {!isAdminRoute && !isAuthRoute && (
        <AnnouncementBanner />
      )}

      {!isAdminRoute && !isAuthRoute && isRegisteredUser && (
        <>
          {shouldListenGameInvites && <GameInviteNotification />}
          {shouldListenTavliPromo && <TavliPromotionListener />}
          <WelcomeBanner />
        </>
      )}



      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Preferences />} />
          <Route path="/imperium1800/settings" element={<StrategoPreferences />} />
          <Route path="/player/:userId" element={<PlayerProfile />} />
          
          <Route path="/chat" element={<ChatLanding />} />
          <Route path="/chat/app" element={<Chat />} />
          <Route path="/chat/shop" element={<ChatShop />} />

          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/tavli" element={<TavliLanding />} />
          <Route path="/tavli-kanones" element={<TavliGuides />} />
          <Route path="/tavli-portes-kanones" element={<TavliPortesRules />} />
          <Route path="/tavli-plakoto-kanones" element={<TavliPlakotoRules />} />
          <Route path="/tavli-feuga-kanones" element={<TavliFevgaRules />} />
          <Route path="/tavli/lobby" element={<TavliLobby />} />
          <Route path="/tavli/:lobbyId" element={<TavliGame />} />


          <Route path="/tavli-embed" element={<TavliEmbed />} />
          <Route path="/imperium1800" element={<StrategoLanding />} />
          <Route path="/imperium1800/lobby" element={<StrategoLobby />} />
          <Route path="/imperium1800/play" element={<StrategoPreview />} />
          <Route path="/imperium1800/game/:lobbyId" element={<StrategoGame />} />
          <Route path="/imperium1800/shop" element={<StrategoShop />} />
          <Route path="/imperium1800/stratigeio" element={<StrategoStratigeio />} />
          <Route path="/imperium1800/stratigeio/setup" element={<StrategoFormationStudio />} />
          <Route path="/imperium1800/pos-paizetai" element={<StrategoHowToPlay />} />
          <Route path="/imperium1800/*" element={<Navigate to="/imperium1800" replace />} />
          <Route path="/ludo" element={<LudoLobby />} />
          <Route path="/ludo/:lobbyId" element={<LudoGame />} />
          <Route path="/kremala" element={<HangmanLobby />} />
          <Route path="/kremala/play" element={<HangmanGame />} />
          <Route path="/kremala/duel" element={<HangmanDuelGame />} />
          <Route path="/kremala/multi/:lobbyId" element={<HangmanMultiGame />} />
          <Route path="/kremala/classic/:lobbyId" element={<HangmanClassicGame />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/about" element={<AboutPlatform />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin-config" element={<AdminBots />} />
          <Route path="/paixnidia" element={<GamesShowcase />} />
          <Route path="/froutakia" element={<SlotsGame />} />
          <Route path="/imperium1800-attack" element={<StrategoSlotsGame />} />
          <Route path="/jukebox-slots" element={<JukeboxSlotsGame />} />
          <Route path="/wheel" element={<WheelOfFate />} />
          <Route path="/arkanoid" element={<ArkanoidGamePage />} />
          <Route path="/agonia" element={<AgoniaLanding />} />
          <Route path="/agonia/play" element={<AgoniaGame />} />
          <Route path="/card-games" element={<CardGames />} />
          <Route path="/card-games/play" element={<CardGames />} />
          <Route path="/kseri" element={<KseriLanding />} />
          <Route path="/kseri/play" element={<XeriGame />} />
          <Route path="/mahjong" element={<MahjongRedirect />} />
          <Route path="/mahjong/" element={<MahjongRedirect />} />
          <Route path="/info-leaderboard" element={<RankingInfo />} />
          <Route path="/embed/game" element={<EmbedGamePage />} />
          <Route path="/preview/gift-card" element={<GiftCardPreviewPage />} />
          <Route path="/dice-lab" element={<DiceLabPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      {!isAdminRoute && <CookieConsent />}
    </>
  );
}

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
